import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { fetchUserSettings, updateUserSettings } from '../api/userSettings';
import { showNotification } from '../components/Notification';

export interface SavedPrompt {
    id: string;
    text: string;
    label?: string;
}

type SavedPromptEntry = string | SavedPrompt;

const toSavedPrompt = (entry: SavedPromptEntry): SavedPrompt => {
    if (typeof entry === 'string') {
        return {
            id: entry,
            text: entry,
        };
    }

    return {
        id: entry.id || entry.text,
        text: entry.text,
        label: entry.label,
    };
};

const normalizeSavedMessages = (entries?: SavedPromptEntry[]): SavedPrompt[] => {
    if (!entries) {
        return [];
    }

    return entries.map(toSavedPrompt);
};

export interface UserSettings {
    user_id: string;
    pinnedAgents: string[];
    recentAgents: string[];
    pinnedConversations: string[];
    preferences: {
        theme: string;
        defaultAgent: string;
    };
    createdAt: string;
    updatedAt: string;
    lastActive: string;
    savedMessages?: SavedPromptEntry[];
    seenAnnouncements?: string[];
}

interface UserSettingsState {
    userSettings: UserSettings | null;
    isLoading: boolean;
    error: string | null;

    loadUserSettings: () => Promise<void>;
    updateUserSettings: (updates: Partial<UserSettings>) => Promise<void>;
    clearError: () => void;

    // Getters
    getPinnedConversations: () => string[];
    isConversationPinned: (conversationID: string) => boolean;
    getSavedMessages: () => SavedPrompt[];
    isMessageSaved: (message: string) => boolean;
    getSeenAnnouncements: () => string[];

    // Setters
    addPinnedConversation: (conversationID: string) => Promise<void>;
    removePinnedConversation: (conversationID: string) => Promise<void>;
    togglePinnedConversation: (conversationID: string, conversationTitle: string) => Promise<void>;
    saveMessage: (message: string) => Promise<void>;
    removeMessage: (message: string) => Promise<void>;
    updateMessageLabel: (message: string, label: string) => Promise<void>;
    markAnnouncementAsSeen: (announcementID: string) => Promise<void>;
}

export const useUserSettingsStore = create<UserSettingsState>()(
    persist(
        (set, get) => ({
            userSettings: null,
            isLoading: false,
            error: null,

            loadUserSettings: async () => {
                set({ isLoading: true, error: null });
                try {
                    const response = await fetchUserSettings();
                    // Ensure savedMessages is always defined
                    const updatedResponse = {
                        ...response,
                        savedMessages: normalizeSavedMessages(response.savedMessages)
                    }
                    set({ userSettings: updatedResponse, isLoading: false });
                } catch (error) {
                    set({ error: 'Failed to fetch user settings', isLoading: false });
                }
            },

            updateUserSettings: async (updates) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await updateUserSettings(updates);
                    // Ensure savedMessages is always defined
                    const updatedResponse = {
                        ...response,
                        savedMessages: normalizeSavedMessages(response.savedMessages)
                    }
                    set({ userSettings: updatedResponse, isLoading: false });
                } catch (error) {
                    set({ error: 'Failed to update user settings', isLoading: false });
                }
            },

            addPinnedConversation: async (conversationID: string) => {
                const currentSettings = get().userSettings;
                if (currentSettings) {
                    const updatedPinnedConversations = [...currentSettings.pinnedConversations, conversationID];
                    set({ userSettings: { ...currentSettings, pinnedConversations: updatedPinnedConversations } });
                    // Persist to backend
                    await get().updateUserSettings({ pinnedConversations: updatedPinnedConversations });
                }
            },

            removePinnedConversation: async (conversationID: string) => {
                const currentSettings = get().userSettings;
                if (currentSettings) {
                    const updatedPinnedConversations = currentSettings.pinnedConversations.filter(id => id !== conversationID);
                    set({ userSettings: { ...currentSettings, pinnedConversations: updatedPinnedConversations } });
                    // Persist to backend
                    await get().updateUserSettings({ pinnedConversations: updatedPinnedConversations });
                }
            },

            saveMessage: async (message: string) => {
                const currentSettings = get().userSettings;
                if (currentSettings) {
                    const updatedSavedMessages = [
                        {
                            id: message,
                            text: message,
                        },
                        ...normalizeSavedMessages(currentSettings.savedMessages)
                    ];
                    set({ userSettings: { ...currentSettings, savedMessages: updatedSavedMessages } });
                    // Persist to backend
                    await get().updateUserSettings({ savedMessages: updatedSavedMessages });
                    showNotification('Prompt Successfully saved to Prompt Library');
                }
            },

            removeMessage: async (message: string) => {
                const currentSettings = get().userSettings;
                if (currentSettings) {
                    const updatedSavedMessages = normalizeSavedMessages(currentSettings.savedMessages)
                        .filter((savedPrompt) => savedPrompt.text !== message);
                    set({ userSettings: { ...currentSettings, savedMessages: updatedSavedMessages } });
                    // Persist to backend
                    await get().updateUserSettings({ savedMessages: updatedSavedMessages });
                }
            },

            updateMessageLabel: async (message: string, label: string) => {
                const currentSettings = get().userSettings;
                if (currentSettings) {
                    const updatedSavedMessages = normalizeSavedMessages(currentSettings.savedMessages)
                        .map((savedPrompt) => {
                            if (savedPrompt.text !== message) {
                                return savedPrompt;
                            }

                            const trimmedLabel = label.trim();
                            return {
                                ...savedPrompt,
                                label: trimmedLabel.length > 0 ? trimmedLabel : undefined,
                            };
                        });

                    set({ userSettings: { ...currentSettings, savedMessages: updatedSavedMessages } });
                    await get().updateUserSettings({ savedMessages: updatedSavedMessages });
                }
            },

            isConversationPinned: (conversationID: string) => {
                const settings = get().userSettings;
                return settings ? settings.pinnedConversations.includes(conversationID) : false;
            },

            getPinnedConversations: () => {
                const settings = get().userSettings;
                return settings ? settings.pinnedConversations : [];
            },
            togglePinnedConversation: async (conversationID: string, conversationTitle: string) => {
                const isPinned = get().getPinnedConversations().includes(conversationID);
                if (isPinned) {
                    get().removePinnedConversation(conversationID);
                    showNotification(`Unpinned - ${conversationTitle}`);
                } else {
                    get().addPinnedConversation(conversationID);
                    showNotification(`Pinned - ${conversationTitle}`);
                }
            },

            getSavedMessages: () => {
                const settings = get().userSettings;
                return normalizeSavedMessages(settings?.savedMessages);
            },

            isMessageSaved: (message: string) => {
                const savedMessages = get().getSavedMessages();
                return savedMessages.some((savedPrompt) => savedPrompt.text === message);
            },

            getSeenAnnouncements: () => {
                const settings = get().userSettings;
                if (settings?.seenAnnouncements === undefined) {
                    return [];
                }
                return settings.seenAnnouncements;
            },

            markAnnouncementAsSeen: async (announcementID: string) => {
                const currentSettings = get().userSettings;
                if (currentSettings) {
                    // Avoid duplicates
                    const updatedSeenAnnouncements = currentSettings.seenAnnouncements && currentSettings.seenAnnouncements.includes(announcementID)
                        ? currentSettings.seenAnnouncements
                        : [...(currentSettings.seenAnnouncements || []), announcementID];
                    set({ userSettings: { ...currentSettings, seenAnnouncements: updatedSeenAnnouncements } });
                    // Persist to backend
                    await get().updateUserSettings({ seenAnnouncements: updatedSeenAnnouncements });
                }
            },

            clearError: () => set({ error: null }),
        }),
        {
            name: 'user-settings-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ userSettings: state.userSettings }),
            // Note: Do NOT add onRehydrateStorage to auto-fetch here.
            // loadUserSettings requires an auth token and fires before
            // MSAL auth completes, causing interaction_in_progress errors.
            // User settings are loaded in __root.tsx after authentication.
        }
    )
);
